import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useOrg } from "../lib/session";
import { defaultWords, fieldTypes, terms, useWorkspace, useWords, type FieldDef, type Term, type TermWords, type Vocabulary } from "../lib/workspace";
import { ErrorText, Loading, PageHeader } from "../components/ui";
import { blankField, FieldEditor } from "../components/FieldEditor";

// Fields & words: the company's own vocabulary and the extra details it
// keeps on contacts and deals.
export default function WorkspaceSettings() {
  const ws = useWorkspace();
  if (ws.isLoading) return <Loading />;
  if (ws.error) return <ErrorText error={ws.error} />;
  return (
    <>
      <PageHeader title="Fields & words" />
      <p className="muted">
        Make SVENEX speak your business's language and keep the details you care about.
        {ws.data!.template && <> Set up from the <b>{ws.data!.template.name}</b> template; anything you change here is yours to keep.</>}
      </p>
      <Words overrides={ws.data!.overrides} />
      <Fields entity="contact" />
      <Fields entity="deal" />
    </>
  );
}

function Words({ overrides }: { overrides: Vocabulary }) {
  const { base, orgId } = useOrg();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Record<Term, TermWords>>(() => {
    const out = {} as Record<Term, TermWords>;
    for (const t of terms) {
      const o = overrides[t.key];
      out[t.key] = { en: { one: o?.en.one ?? "", other: o?.en.other ?? "" }, ar: { one: o?.ar.one ?? "", other: o?.ar.other ?? "" } };
    }
    return out;
  });
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const set = (term: Term, lang: "en" | "ar", form: "one" | "other", v: string) => {
    setSaved(false);
    setDraft((d) => ({ ...d, [term]: { ...d[term], [lang]: { ...d[term][lang], [form]: v } } }));
  };
  async function save() {
    setError(null);
    const vocabulary: Vocabulary = {};
    for (const t of terms) {
      const d = draft[t.key];
      if (d.en.one || d.en.other || d.ar.one || d.ar.other) vocabulary[t.key] = d;
    }
    try {
      await api(`${base}/workspace/vocabulary`, { method: "PUT", json: { vocabulary } });
      qc.invalidateQueries({ queryKey: [orgId, "workspace"] });
      setSaved(true);
    } catch (err) {
      setError(err);
    }
  }
  return (
    <section className="card">
      <h2>Words</h2>
      <p className="muted small">What your team and customers call things. Leave a box empty to keep the default (shown in grey). The AI assistant uses these words too.</p>
      <table className="table words-table">
        <thead><tr><th></th><th>English</th><th>English plural</th><th>Arabic</th><th>Arabic plural</th></tr></thead>
        <tbody>
          {terms.map((t) => (
            <tr key={t.key}>
              <th scope="row">{t.label}</th>
              <td><input aria-label={`${t.label}: English`} value={draft[t.key].en.one} maxLength={40} placeholder={defaultWords[t.key].en.one} onChange={(e) => set(t.key, "en", "one", e.target.value)} /></td>
              <td><input aria-label={`${t.label}: English plural`} value={draft[t.key].en.other} maxLength={40} placeholder={defaultWords[t.key].en.other} onChange={(e) => set(t.key, "en", "other", e.target.value)} /></td>
              <td><input aria-label={`${t.label}: Arabic`} dir="rtl" value={draft[t.key].ar.one} maxLength={40} placeholder={defaultWords[t.key].ar.one} onChange={(e) => set(t.key, "ar", "one", e.target.value)} /></td>
              <td><input aria-label={`${t.label}: Arabic plural`} dir="rtl" value={draft[t.key].ar.other} maxLength={40} placeholder={defaultWords[t.key].ar.other} onChange={(e) => set(t.key, "ar", "other", e.target.value)} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <ErrorText error={error} />
      <div className="inline">
        <button onClick={save}>Save words</button>
        {saved && <span className="muted small">Saved.</span>}
      </div>
    </section>
  );
}

function Fields({ entity }: { entity: "contact" | "deal" }) {
  const { base, orgId } = useOrg();
  const qc = useQueryClient();
  const w = useWords();
  const key = [orgId, "fields", entity];
  const list = useQuery({ queryKey: key, queryFn: () => api<{ fields: FieldDef[] }>(`${base}/fields?entity=${entity}&archived=1`) });
  const [adding, setAdding] = useState<FieldDef | null>(null);
  const [editing, setEditing] = useState<{ id: string; def: FieldDef; keep: string[] } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: key });
    qc.invalidateQueries({ queryKey: [orgId, "workspace"] });
  };
  async function run(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      refresh();
      return true;
    } catch (err) {
      setError(err);
      return false;
    }
  }
  const fields = list.data?.fields ?? [];
  const active = fields.filter((f) => !f.archived);
  const archived = fields.filter((f) => f.archived);
  const move = (i: number, dir: -1 | 1) => {
    const ids = active.map((f) => f.id!);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    run(() => api(`${base}/fields/order`, { method: "PUT", json: { entity, ids } }));
  };
  const typeLabel = (t: string) => fieldTypes.find((x) => x.value === t)?.label ?? t;
  const title = entity === "contact" ? `${w("contact")} fields` : `${w("deal")} fields`;

  return (
    <section className="card">
      <div className="inline" style={{ justifyContent: "space-between" }}>
        <h2 style={{ margin: 0 }}>{title}</h2>
        {!adding && <button className="secondary" onClick={() => { setEditing(null); setAdding(blankField()); }}>Add field</button>}
      </div>
      {list.isLoading && <Loading />}
      {!list.isLoading && active.length === 0 && !adding && <p className="muted small">No extra fields yet.</p>}
      <ul className="field-list list">
        {active.map((f, i) => (
          <li key={f.id}>
            {editing && editing.id === f.id ? (
              <div className="grow">
                <FieldEditor value={editing.def} saved keepValues={editing.keep} onChange={(def) => setEditing((x) => x && { ...x, def })} />
                <div className="inline">
                  <button onClick={async () => {
                    const d = editing.def;
                    if (await run(() => api(`${base}/fields/${f.id}`, { method: "PATCH", json: { label_en: d.label_en, label_ar: d.label_ar, options: d.options, ai_fillable: d.ai_fillable } }))) setEditing(null);
                  }}>Save field</button>
                  <button className="secondary" onClick={() => setEditing(null)}>Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <div className="grow">
                  <b>{f.label_en}</b>{f.label_ar && <span className="muted"> · <span dir="rtl">{f.label_ar}</span></span>}
                  <div className="muted small">
                    {typeLabel(f.type)}
                    {f.options.length > 0 && ` · ${f.options.map((o) => o.label_en).join(", ")}`}
                    {f.ai_fillable && <span className="tag ai-tag">AI fills</span>}
                  </div>
                </div>
                <button className="link small" aria-label={`Move ${f.label_en} up`} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                <button className="link small" aria-label={`Move ${f.label_en} down`} disabled={i === active.length - 1} onClick={() => move(i, 1)}>↓</button>
                <button className="link small" onClick={() => { setAdding(null); setEditing({ id: f.id!, def: { ...f, options: f.options.map((o) => ({ ...o })) }, keep: f.options.map((o) => o.value) }); }}>Edit</button>
                <button className="link small" onClick={() => confirm(`Archive "${f.label_en}"? It disappears from forms; values already saved are kept and come back if you restore it.`) && run(() => api(`${base}/fields/${f.id}`, { method: "PATCH", json: { archived: true } }))}>Archive</button>
              </>
            )}
          </li>
        ))}
      </ul>
      {adding && (
        <div className="card inset">
          <FieldEditor value={adding} onChange={setAdding} />
          <div className="inline">
            <button disabled={!adding.label_en.trim()} onClick={async () => {
              if (await run(() => api(`${base}/fields`, { method: "POST", json: { entity, ...adding } }))) setAdding(null);
            }}>Create field</button>
            <button className="secondary" onClick={() => setAdding(null)}>Cancel</button>
          </div>
        </div>
      )}
      <ErrorText error={error} />
      {archived.length > 0 && (
        <details className="small" style={{ marginTop: ".75rem" }}>
          <summary className="muted">Archived ({archived.length})</summary>
          <ul className="field-list list">
            {archived.map((f) => (
              <li key={f.id} className="archived">
                <span className="grow">{f.label_en} <span className="muted">· {typeLabel(f.type)}</span></span>
                <button className="link small" onClick={() => run(() => api(`${base}/fields/${f.id}`, { method: "PATCH", json: { archived: false } }))}>Restore</button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
